import {
  motion,
} from "framer-motion";

import {
  Link,
} from "react-router-dom";

import {
  useTranslation,
} from "react-i18next";

import siteConfig from "../../../site/config/site";

import {
  getRoutePath,
} from "../../../site/config/routes";

/*
  ============================================================
  PHOTOGRAPHY PREMIUM
  CONTACT / FINAL CTA
  ============================================================

  Etusivun viimeinen varsinainen sisältöosio.

  Ivory:
  - tumma päätösosio

  Noir:
  - vaalea päätösosio

  Varsinaiset yhteystiedot tulevat siteConfigista.
*/

export default function ContactSection({
  language = "fi",
}) {
  const {
    t,
  } = useTranslation("common");

  const email =
    siteConfig.contact?.email ||
    "";

  const instagram =
    siteConfig.socials?.instagram ||
    "";

  return (
    <section
      id="contact"
      className="
        relative
        overflow-hidden
        bg-text
        text-canvas
      "
    >
      <div
        className="
          mx-auto
          flex
          min-h-[80svh]
          w-full
          max-w-editorial
          flex-col
          justify-between
          px-6
          py-20

          sm:px-10
          sm:py-28

          lg:min-h-[88svh]
          lg:px-16
          lg:py-32
        "
      >
        {/* ====================================================
            META
            ==================================================== */}

        <motion.div
          className="
            flex
            items-center
            justify-between
            gap-8
            border-b
            border-canvas/20
            pb-5
          "
          initial={{
            opacity: 0,
            y: 16,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.5,
          }}
          transition={{
            duration: 0.75,
            ease: [
              0.22,
              1,
              0.36,
              1,
            ],
          }}
        >
          <p
            className="
              m-0
              text-[9px]
              font-medium
              uppercase
              tracking-[0.28em]
              text-canvas/65

              sm:text-[10px]
            "
          >
            06 /{" "}
            {t(
              "contactSection.eyebrow"
            )}
          </p>

          <span
            className="
              h-px
              w-12
              bg-canvas/25

              sm:w-20
            "
            aria-hidden="true"
          />
        </motion.div>

        {/* ====================================================
            MAIN
            ==================================================== */}

        <div
          className="
            grid
            gap-14
            py-20

            sm:py-28

            lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.32fr)]
            lg:items-end
            lg:gap-20
            lg:py-32
          "
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 36,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.3,
            }}
            transition={{
              duration: 1,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <p
              className="
                mb-7
                text-[9px]
                font-medium
                uppercase
                tracking-[0.26em]
                text-canvas/60

                sm:text-[10px]
              "
            >
              {t(
                "contactSection.intro"
              )}
            </p>

            <h2
              className="
                m-0
                max-w-[1100px]
                font-display
                text-[clamp(4rem,10vw,10rem)]
                font-normal
                leading-[0.82]
                tracking-[-0.055em]
              "
            >
              {t(
                "contactSection.title"
              )}
            </h2>
          </motion.div>

          <motion.div
            className="
              max-w-md

              lg:pb-2
            "
            initial={{
              opacity: 0,
              y: 24,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
              amount: 0.45,
            }}
            transition={{
              duration: 0.85,
              delay: 0.12,
              ease: [
                0.22,
                1,
                0.36,
                1,
              ],
            }}
          >
            <div
              className="
                mb-7
                h-px
                w-10
                bg-canvas
              "
              aria-hidden="true"
            />

            <p
              className="
                m-0
                text-sm
                leading-7
                text-canvas/70

                sm:text-base
                sm:leading-8
              "
            >
              {t(
                "contactSection.description"
              )}
            </p>

            <div
              className="
                mt-9
                flex
                flex-col
                items-start
                gap-3

                sm:flex-row
                sm:flex-wrap

                lg:flex-col
              "
            >
              <Link
                to={getRoutePath(
                  "contact",
                  language
                )}
                className="
                  inline-flex
                  min-h-12
                  items-center
                  justify-center
                  border
                  border-canvas
                  bg-canvas
                  px-6
                  py-3
                  text-[10px]
                  font-semibold
                  uppercase
                  tracking-[0.18em]
                  text-text
                  no-underline
                  transition
                  duration-300

                  hover:bg-transparent
                  hover:text-canvas
                "
              >
                {t(
                  "contactSection.contact"
                )}
              </Link>

              <Link
                to={getRoutePath(
                  "services",
                  language
                )}
                className="
                  group
                  inline-flex
                  min-h-12
                  items-center
                  gap-3
                  border
                  border-canvas/40
                  px-6
                  py-3
                  text-[10px]
                  font-semibold
                  uppercase
                  tracking-[0.18em]
                  text-canvas
                  no-underline
                  transition
                  duration-300

                  hover:border-canvas
                "
              >
                {t(
                  "contactSection.sessions"
                )}

                <span
                  className="
                    transition-transform
                    duration-300
                    group-hover:translate-x-1
                  "
                  aria-hidden="true"
                >
                  →
                </span>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* ====================================================
            DETAILS
            ==================================================== */}

        <motion.div
          className="
            flex
            flex-col
            gap-6
            border-t
            border-canvas/20
            pt-6

            sm:flex-row
            sm:items-end
            sm:justify-between
          "
          initial={{
            opacity: 0,
          }}
          whileInView={{
            opacity: 1,
          }}
          viewport={{
            once: true,
            amount: 0.7,
          }}
          transition={{
            duration: 0.9,
            delay: 0.1,
          }}
        >
          <div
            className="
              flex
              flex-wrap
              gap-x-8
              gap-y-3
            "
          >
            {email && (
              <a
                href={`mailto:${email}`}
                className="
                  text-[9px]
                  uppercase
                  tracking-[0.2em]
                  text-canvas/70
                  no-underline
                  transition-colors
                  hover:text-canvas

                  sm:text-[10px]
                "
              >
                {email}
              </a>
            )}

            {instagram && (
              <a
                href={instagram}
                target="_blank"
                rel="noreferrer"
                className="
                  text-[9px]
                  uppercase
                  tracking-[0.2em]
                  text-canvas/70
                  no-underline
                  transition-colors
                  hover:text-canvas

                  sm:text-[10px]
                "
              >
                Instagram
              </a>
            )}
          </div>

          <p
            className="
              m-0
              text-[9px]
              uppercase
              tracking-[0.22em]
              text-canvas/45

              sm:text-[10px]
            "
          >
            {siteConfig.brand.location}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
